/**
 * core · o próprio usuário (spec 03 §2 · tela 2.3).
 *
 * `/me` é sempre o dono da sessão — não existe `/me?id=`. A tela 2.3 nunca abre o
 * perfil de terceiro, e a administração de outras pessoas vive em 10.1/10.3, com
 * outro módulo de permissão.
 */
import { z } from 'zod'
import { senha, uuid } from './comum.js'
import { celulaDaMatriz } from './niveis.js'
import { idDeRelatorio } from './relatorios.js'

/**
 * Telefone BR mascarado (D-66 · GLOSSARIO §Formatação).
 *
 * A validação é sobre o valor MASCARADO, e não sobre os dígitos: é o que a tela
 * exibe e o que a pessoa confere. Aceitar "51999998888" e guardar mascarado faria o
 * mesmo número existir em dois formatos no banco.
 */
export const telefoneBr = z
  .string()
  .regex(/^\(\d{2}\) \d{4,5}-\d{4}$/, 'Use o formato (00) 00000-0000.')

export const preferenciasGerais = z.object({
  idioma: z.enum(['pt-BR', 'es-419', 'en-US']),
  fusoHorario: z.string(),
  entidadePadraoId: uuid.nullable(),
  formatoDeData: z.enum(['DD/MM/AAAA', 'AAAA-MM-DD', 'MM/DD/AAAA']),
  tema: z.enum(['claro', 'escuro', 'automatico']),
  /**
   * Os favoritos da 8.7 (spec 02 §1.7 — a coluna `preferencias` os declara desde a
   * migration 0004).
   *
   * Ficam aqui, e não em `sessionStorage` como no protótipo, porque a preferência é
   * DA PESSOA e não do navegador: quem estrela a DRE no laptop encontra a DRE
   * estrelada no celular. Default vazio — o protótipo semeava `['dre','rent','mrr']`
   * e semente de mock não vira dado (D-65).
   */
  favoritosRelatorios: z.array(idDeRelatorio),
})
export type PreferenciasGerais = z.infer<typeof preferenciasGerais>

export const PREFERENCIAS_PADRAO: PreferenciasGerais = {
  idioma: 'pt-BR',
  fusoHorario: 'America/Sao_Paulo',
  entidadePadraoId: null,
  formatoDeData: 'DD/MM/AAAA',
  tema: 'claro',
  favoritosRelatorios: [],
}

export const perfil = z.object({
  id: uuid,
  nome: z.string(),
  email: z.string(),
  funcaoLivre: z.string().nullable(),
  telefone: z.string().nullable(),
  bio: z.string().nullable(),
  nivelAcessoNome: z.string(),
  /**
   * A matriz efetiva de quem está na sessão (lote F1 · achado PED-01).
   *
   * A tela precisava responder "esta pessoa pode registrar uma nota?" para não oferecer
   * um botão que o servidor recusaria — e não tinha como: `/me` dizia o NOME do nível e
   * nada do que ele concede. A alternativa seria a tela adivinhar por nome de nível
   * ("Financeiro pode faturar"), que é uma segunda regra de permissão, escrita em outro
   * lugar, fadada a divergir da primeira.
   *
   * Aqui vai a MESMA estrutura que o motor de decisão do servidor consome, para a tela
   * chamar `alcanca()` de `@dv/domain` — a mesma função do guard. O servidor continua
   * sendo quem decide: isto é para não OFERECER o que será recusado, nunca para liberar.
   */
  permissoes: z.array(celulaDaMatriz),
  preferencias: preferenciasGerais,
  /**
   * O avatar da 2.3 (passo 7.1). É um ANEXO como qualquer outro — `entidade_tipo =
   * 'usuario_interno'` —, e não uma coluna com um caminho de arquivo: assim ele
   * herda o upload, o limite de tamanho e o download assinado que já existem, em vez
   * de ganhar um segundo mecanismo de arquivo só para foto de perfil.
   */
  avatarAnexoId: uuid.nullable(),
  /** Estado do segundo fator, para o bloco "Verificação em duas etapas" da 2.3. */
  doisFatoresObrigatorio: z.boolean(),
  temSegundoFator: z.boolean(),
  segundoFatorDesde: z.string().nullable(),
})
export type Perfil = z.infer<typeof perfil>

export const entradaDePerfil = z.object({
  nome: z.string().min(2, 'O nome precisa de ao menos 2 caracteres.').optional(),
  funcaoLivre: z.string().nullish(),
  telefone: telefoneBr.nullish(),
  bio: z.string().nullish(),
  preferencias: preferenciasGerais.partial().optional(),
  /** `null` remove a foto; o anexo em si continua no bloco de anexos da pessoa. */
  avatarAnexoId: uuid.nullish(),
})
export type EntradaDePerfil = z.infer<typeof entradaDePerfil>

/**
 * Troca de senha — a atual é obrigatória.
 *
 * Sem ela, uma sessão esquecida aberta trocaria a senha do dono e o expulsaria da
 * própria conta.
 */
export const entradaDeTrocaDeSenha = z.object({
  senhaAtual: z.string().min(1, 'Informe a senha atual.'),
  senhaNova: senha,
})
export type EntradaDeTrocaDeSenha = z.infer<typeof entradaDeTrocaDeSenha>
